/**
 * App-wide toast/snackbar mechanism.
 *
 * A small provider exposes `useToast()` so later specs can raise success/error
 * toasts after writes. Toasts render as MUI `Snackbar` + `Alert`, dark per the
 * design tokens (handoff "Design Tokens").
 *
 * The stack is bounded (NOTIF-003/004, TMMRSL-0073): at most three show at once. A fourth replaces
 * the oldest one that isn't an error; when all three are errors it waits in a queue and shows as
 * soon as one is dismissed. A success auto-dismisses after ~4.2 s; an error stays until the person
 * dismisses it. Each toast is an `Alert` (`role="alert"`), as before.
 */
import { Alert, Snackbar, useTheme } from '@mui/material';
import { useCallback, useRef, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { resolveTokens, SHADOWS } from '@/theme';
import { ToastContext, type ShowToast, type ToastSeverity } from './toastContext';

interface ToastItem {
  id: number;
  message: string;
  severity: ToastSeverity;
}

interface ToastStack {
  visible: ToastItem[];
  queued: ToastItem[];
}

/** Auto-dismiss duration (ms) for a success — matches the handoff's ~4.2s. Errors never auto-hide. */
const AUTO_HIDE_MS = 4200;
/** The most toasts on screen at once. */
const MAX_VISIBLE = 3;

/** The stack after a new toast: shown, replacing the oldest non-error at the limit, or queued. */
function pushToast(stack: ToastStack, item: ToastItem): ToastStack {
  if (stack.visible.length < MAX_VISIBLE) return { ...stack, visible: [...stack.visible, item] };
  const oldest = stack.visible.findIndex((toast) => toast.severity !== 'error');
  if (oldest < 0) return { ...stack, queued: [...stack.queued, item] };
  return {
    ...stack,
    visible: [...stack.visible.slice(0, oldest), ...stack.visible.slice(oldest + 1), item],
  };
}

/** The stack after a toast closes: the next queued one, if any, takes its place. */
function dismissToast(stack: ToastStack, id: number): ToastStack {
  const visible = stack.visible.filter((toast) => toast.id !== id);
  const [next, ...rest] = stack.queued;
  if (next === undefined || visible.length >= MAX_VISIBLE) return { ...stack, visible };
  return { visible: [...visible, next], queued: rest };
}

export function ToastProvider({ children }: { children: ReactNode }) {
  const { t } = useTranslation();
  const tokens = resolveTokens(useTheme());
  const [stack, setStack] = useState<ToastStack>({ visible: [], queued: [] });
  const idRef = useRef(0);

  const show = useCallback<ShowToast>((message, severity = 'success') => {
    const id = (idRef.current += 1);
    setStack((current) => pushToast(current, { id, message, severity }));
  }, []);

  const dismiss = useCallback((id: number) => {
    setStack((current) => dismissToast(current, id));
  }, []);

  return (
    <ToastContext.Provider value={show}>
      {children}
      {stack.visible.map((toast, i) => (
        <Snackbar
          key={toast.id}
          open
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          autoHideDuration={toast.severity === 'error' ? null : AUTO_HIDE_MS}
          onClose={(_event, reason) => {
            // Ignore click-away so stacked toasts don't dismiss each other.
            if (reason === 'clickaway') return;
            dismiss(toast.id);
          }}
          // Stack multiple toasts vertically.
          sx={{ mb: `${i * 60}px` }}
        >
          <Alert
            onClose={() => dismiss(toast.id)}
            closeText={t('common.close')}
            severity={toast.severity}
            variant="filled"
            sx={{
              bgcolor: tokens.surfaceRaised,
              color: tokens.text,
              boxShadow: SHADOWS.snackbar,
              '& .MuiAlert-icon': {
                color:
                  toast.severity === 'error'
                    ? tokens.errorFg
                    : toast.severity === 'warning'
                      ? tokens.warningFg
                      : tokens.successFg,
              },
            }}
          >
            {toast.message}
          </Alert>
        </Snackbar>
      ))}
    </ToastContext.Provider>
  );
}
